import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Loader2, Sparkles, BarChart3, Database } from "lucide-react";
import { apiMutate } from "@/lib/api";
import { setLeadGateTrigger } from "@/App";

export function LeadGateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const showGate = useCallback(() => {
    // Don't show if cookie already exists (race condition guard)
    if (document.cookie.includes("demo_lead=")) return;
    setOpen(true);
  }, []);

  // Register the trigger so QueryCache can invoke it
  useEffect(() => {
    setLeadGateTrigger(showGate);
    return () => setLeadGateTrigger(null);
  }, [showGate]);

  return (
    <>
      {children}
      {open && <LeadCaptureModal onClose={() => setOpen(false)} />}
    </>
  );
}

const FEATURES = [
  { icon: Sparkles, label: "AI Data Analyst" },
  { icon: BarChart3, label: "Advanced Analytics" },
  { icon: Database, label: "Data Explorer" },
];

function LeadCaptureModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Show native dialog on mount
  const setRef = useCallback(
    (node: HTMLDialogElement | null) => {
      (dialogRef as React.MutableRefObject<HTMLDialogElement | null>).current = node;
      if (node && !node.open) node.showModal();
    },
    []
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const form = new FormData(e.currentTarget);
    const email = (form.get("email") as string)?.trim();

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }

    try {
      await apiMutate("/api/leads", "POST", {
        email,
        name: (form.get("name") as string)?.trim() || undefined,
        company: (form.get("company") as string)?.trim() || undefined,
        source: "lead_gate",
      });

      onClose();
      // Retry all failed queries now that the cookie is set
      queryClient.invalidateQueries();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <dialog
      ref={setRef}
      className="fixed inset-0 z-50 m-0 h-full w-full max-h-full max-w-full border-none bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-sm open:flex open:items-center open:justify-center"
      onCancel={(e) => e.preventDefault()}
    >
      <div className="w-full max-w-md mx-4 rounded-xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">
            Unlock Full Access
          </h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Enter your details to access advanced features:
        </p>

        <div className="flex gap-3 mb-5">
          {FEATURES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-1 items-center gap-1.5 rounded-lg border border-border/50 bg-muted/30 px-2.5 py-2 text-xs text-muted-foreground"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{label}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="lead-email" className="sr-only">
              Email
            </label>
            <input
              id="lead-email"
              name="email"
              type="email"
              required
              autoFocus
              placeholder="Work email"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="lead-name" className="sr-only">
                Name
              </label>
              <input
                id="lead-name"
                name="name"
                type="text"
                placeholder="Name (optional)"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div>
              <label htmlFor="lead-company" className="sr-only">
                Company
              </label>
              <input
                id="lead-company"
                name="company"
                type="text"
                placeholder="Company (optional)"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Get Access"
            )}
          </button>
        </form>

        <p className="mt-3 text-center text-[11px] text-muted-foreground/50">
          Demo platform with synthetic data. No account required.
        </p>
      </div>
    </dialog>
  );
}
